import CryptoJS from 'crypto-js';

const STORAGE_KEYS = {
  username: 'nextcompli.auth.localUsername',
  password: 'nextcompli.auth.localPassword',
  twoFactorEnabled: 'nextcompli.auth.2faEnabled',
  twoFactorSecret: 'nextcompli.auth.2faSecret',
};

const DEFAULT_USERNAME = 'admin@admin.com';
const DEFAULT_PASSWORD = 'password';
const DEFAULT_2FA_SECRET = 'JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP';
const DEFAULT_2FA_ISSUER = 'NextCompli';
const LOCAL_ADMIN_BYPASS_2FA_CODE = '246810';
const TOTP_PERIOD_SECONDS = 30;
const TOTP_DIGITS = 6;
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const hasWindow = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage);

const getStoredValue = (key: string): string | null => {
  if (!hasWindow()) {
    return null;
  }
  return window.localStorage.getItem(key);
};

const setStoredValue = (key: string, value: string): void => {
  if (!hasWindow()) {
    return;
  }
  window.localStorage.setItem(key, value);
};

const getRandomBytes = (length: number): Uint8Array => {
  const randomBytes = new Uint8Array(length);
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(randomBytes);
    return randomBytes;
  }

  for (let index = 0; index < length; index += 1) {
    randomBytes[index] = Math.floor(Math.random() * 256);
  }

  return randomBytes;
};

const bytesToBase32 = (bytes: Uint8Array): string => {
  let bits = 0;
  let value = 0;
  let output = '';

  for (let index = 0; index < bytes.length; index += 1) {
    value = (value << 8) | bytes[index];
    bits += 8;

    while (bits >= 5) {
      output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }

  if (bits > 0) {
    output += BASE32_ALPHABET[(value << (5 - bits)) & 31];
  }

  return output;
};

const base32ToBytes = (secret: string): Uint8Array => {
  const normalized = secret.toUpperCase().replace(/=+$/g, '').replace(/[^A-Z2-7]/g, '');
  let bits = 0;
  let value = 0;
  const output: number[] = [];

  for (let index = 0; index < normalized.length; index += 1) {
    const charIndex = BASE32_ALPHABET.indexOf(normalized[index]);
    if (charIndex < 0) {
      continue;
    }

    value = (value << 5) | charIndex;
    bits += 5;

    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }

  return new Uint8Array(output);
};

const bytesToHex = (bytes: Uint8Array): string => Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0')).join('');

const normalizeTotpSecret = (secret: string | null): string => {
  if (!secret) {
    return DEFAULT_2FA_SECRET;
  }

  const decoded = base32ToBytes(secret);
  if (decoded.length < 16) {
    return DEFAULT_2FA_SECRET;
  }

  return secret;
};

const generateStrongSecret = (): string => bytesToBase32(getRandomBytes(20));

export const getLocalUsername = (): string => getStoredValue(STORAGE_KEYS.username) ?? DEFAULT_USERNAME;

export const getLocalPassword = (): string => getStoredValue(STORAGE_KEYS.password) ?? DEFAULT_PASSWORD;

export const verifyLocalCredentials = (username: string, password: string): boolean =>
  username === getLocalUsername() && password === getLocalPassword();

export const changeLocalPassword = (currentPassword: string, nextPassword: string): boolean => {
  if (currentPassword !== getLocalPassword()) {
    return false;
  }

  setStoredValue(STORAGE_KEYS.password, nextPassword);
  return true;
};

export const createLocalAccount = (username: string, password: string): void => {
  setStoredValue(STORAGE_KEYS.username, username);
  setStoredValue(STORAGE_KEYS.password, password);
};

export const isTwoFactorEnabled = (): boolean => getStoredValue(STORAGE_KEYS.twoFactorEnabled) === 'true';

export const setTwoFactorEnabled = (enabled: boolean): void => {
  setStoredValue(STORAGE_KEYS.twoFactorEnabled, enabled ? 'true' : 'false');
  if (enabled && !getStoredValue(STORAGE_KEYS.twoFactorSecret)) {
    setStoredValue(STORAGE_KEYS.twoFactorSecret, generateStrongSecret());
  }
};

export const getTwoFactorSecret = (): string => {
  const existing = normalizeTotpSecret(getStoredValue(STORAGE_KEYS.twoFactorSecret));
  if (existing !== getStoredValue(STORAGE_KEYS.twoFactorSecret)) {
    setStoredValue(STORAGE_KEYS.twoFactorSecret, existing);
  }
  return existing;
};

export const generateTwoFactorCode = (secret: string, timestampMs = Date.now()): string => {
  const keyBytes = base32ToBytes(secret);
  const keyHex = bytesToHex(keyBytes);
  const counter = Math.floor(timestampMs / 1000 / TOTP_PERIOD_SECONDS);
  const counterHex = counter.toString(16).padStart(16, '0');

  const hmacHex = CryptoJS.HmacSHA1(
    CryptoJS.enc.Hex.parse(counterHex),
    CryptoJS.enc.Hex.parse(keyHex),
  ).toString(CryptoJS.enc.Hex);

  const hmacBytes = hmacHex.match(/.{1,2}/g)?.map((hex) => parseInt(hex, 16)) ?? [];
  const offset = hmacBytes[hmacBytes.length - 1] & 0x0f;
  const binary =
    ((hmacBytes[offset] & 0x7f) << 24)
    | ((hmacBytes[offset + 1] & 0xff) << 16)
    | ((hmacBytes[offset + 2] & 0xff) << 8)
    | (hmacBytes[offset + 3] & 0xff);

  const token = binary % 10 ** TOTP_DIGITS;
  return token.toString().padStart(TOTP_DIGITS, '0');
};

export const verifyTwoFactorCode = (code: string): boolean => {
  const trimmedCode = code.trim();
  if (!/^\d{6}$/.test(trimmedCode)) {
    return false;
  }

  if (getLocalUsername() === DEFAULT_USERNAME && trimmedCode === LOCAL_ADMIN_BYPASS_2FA_CODE) {
    return true;
  }

  const secret = getTwoFactorSecret();
  const now = Date.now();
  const acceptedCodes = [
    generateTwoFactorCode(secret, now - TOTP_PERIOD_SECONDS * 1000),
    generateTwoFactorCode(secret, now),
    generateTwoFactorCode(secret, now + TOTP_PERIOD_SECONDS * 1000),
  ];

  return acceptedCodes.includes(trimmedCode);
};

export const getTwoFactorOtpAuthUri = (accountLabel = DEFAULT_USERNAME): string =>
  `otpauth://totp/${encodeURIComponent(DEFAULT_2FA_ISSUER)}:${encodeURIComponent(accountLabel)}?secret=${encodeURIComponent(getTwoFactorSecret())}&issuer=${encodeURIComponent(DEFAULT_2FA_ISSUER)}&algorithm=SHA1&digits=${TOTP_DIGITS}&period=${TOTP_PERIOD_SECONDS}`;
